import { FC, useState, useEffect } from 'react'
import { observer } from 'mobx-react'
import 'react-confirm-alert/src/react-confirm-alert.css'
import { SortableHandle, SortableElement } from 'react-sortable-hoc'
import { confirmAlert } from 'react-confirm-alert'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'

import PortionEditor from './PortionEditor'

import { Portion } from '../../models3/Portion'
import { Project } from '../../models3/Project'
import { Root } from '../../models3/Root'

import { RootConsumer, useAppRoot } from '../app/RootContext'
import { ExportModal } from '../export/ExportModal'
import { PortionNotificationList } from '../notifications/Notifications'

import { CopyButton, DownloadButton } from '../utils/Buttons'
import { CopyProjectDataModal } from '../utils/CopyProjectDataModal'
import { displayError } from '../utils/Errors'

import './Portion.css'

// eslint-disable-next-line @typescript-eslint/no-var-requires
const log = require('debug')('sltt:PortionView')

const DragHandle = SortableHandle(() => {
    const { t } = useTranslation()

    return (
        <RootConsumer>
            {() => (
                <span className="portion-handle" data-toggle="tooltip" title={t('Drag to reorder.')}>
                    <i className="fas fa-bars" />
                </span>
            )}
        </RootConsumer>
    )
})

interface PortionNameProps {
    portion: Portion
    setEditing: (value: boolean) => void
}

const PortionName = observer(({ portion, setEditing }: PortionNameProps) => {
    const [trackedProject, setTrackedProject] = useState<Project | undefined>(undefined)
    const appRoot = useAppRoot()
    const { t } = useTranslation()

    useEffect(() => {
        async function getTrackedProject() {
            if (portion.trackedProjectName.trim() !== '') {
                const trackedRt = appRoot.rts.find((r) => r.name === portion.trackedProjectName)
                await trackedRt?.initialize()
                setTrackedProject(trackedRt?.project)
            } else {
                setTrackedProject(undefined)
            }
        }

        getTrackedProject()
    }, [trackedProject?.displayName, portion.trackedProjectName, appRoot.rts])

    if (trackedProject) {
        const tooltip = (
            <Tooltip id="passage-tooltip">
                {portion.name}
                <div>{t('Copied from project {{projectName}}', { projectName: trackedProject.displayName })}</div>
            </Tooltip>
        )

        return (
            <OverlayTrigger overlay={tooltip} placement="bottom" delayShow={1000}>
                <div className="portion-name" data-id={portion.name} onDoubleClick={() => setEditing(true)}>
                    {portion.name}
                </div>
            </OverlayTrigger>
        )
    }

    return (
        <div className="portion-name" data-id={portion.name} onDoubleClick={() => setEditing(true)}>
            {portion.name}
        </div>
    )
})

interface PortionMenuProps {
    rt: Root
    portion: Portion
    setCopyModalOpen: (value: boolean) => void
    onDelete: () => void
    setEditing: (edit: boolean) => void
}

const PortionMenu: FC<PortionMenuProps> = ({ rt, portion, onDelete, setEditing, setCopyModalOpen }) => {
    const [exportModalOpen, setExportModalOpen] = useState(false)

    const { t } = useTranslation()

    return (
        <div className="portion-menu">
            {exportModalOpen && (
                <ExportModal rt={rt} exportFromType="portion" setOpen={setExportModalOpen} portion={portion} />
            )}
            <span
                className="portion-button"
                data-toggle="tooltip"
                data-id={`edit-${portion.name}`}
                title={t('Edit')}
                onClick={(e) => {
                    e.preventDefault()
                    setEditing(true)
                }}
            >
                <i className="fas fa-fw fa-pencil-alt" />
            </span>
            <span
                className="portion-button"
                data-toggle="tooltip"
                data-id={`delete-${portion.name}`}
                title={t('Delete portion.')}
                onClick={(e) => {
                    e.preventDefault()
                    onDelete()
                }}
            >
                <i className="fas fa-fw fa-trash-alt" />
            </span>

            {/* <span
                className="portion-button portion-download-button"
                data-toggle="tooltip"
                data-id={`download-${portion.name}`}
                title={t('recordingDownloadRecordingsAndNotes')}
                onClick={(e) => {
                    e.preventDefault()
                    downloadVideos()
                }}
            >
                <i className="fas fa-fw fa-cloud-download-alt" />
            </span> */}

            <CopyButton
                onClick={() => setCopyModalOpen(true)}
                buttonClassName=""
                className="portion-button"
                tooltip={t('Copy portion')}
                enabled
            />

            <DownloadButton
                className="portion-button portion-download-button"
                tooltip={t('Export')}
                onClick={() => setExportModalOpen(true)}
                enabled
            />
        </div>
    )
}

interface IPortionView {
    rt: Root
    portion: Portion
    onDelete: () => void
}

const PortionView: FC<IPortionView> = observer(({ rt, portion, onDelete }) => {
    const [editing, setEditing] = useState(false)
    const [copyModalOpen, setCopyModalOpen] = useState(false)
    // const { t } = useTranslation()

    // // Schedule download of latest videos for all passages in this portion.
    // // Also schedule downloads for the notes videos for these passage videos.
    // async function downloadVideos() {
    //     let downloadCount = 0
    //     for (const passage of portion.passages) {
    //         const video = passage.getDefaultVideo('')
    //         if (video) {
    //             downloadCount += (await VideoCache.videoCacheDownloader.implicitVideoDownload(video.url)) ? 0 : 1

    //             for (const note of video.notes) {
    //                 for (const item of note.items) {
    //                     if (item.url) {
    //                         downloadCount += (await VideoCache.videoCacheDownloader.implicitVideoDownload(item.url))
    //                             ? 0
    //                             : 1
    //                     }
    //                 }
    //             }
    //         }
    //     }
    //     displayInfo(t('recordingNotYetDownloaded', { downloadCount: downloadCount.toString() }))
    // }

    const { iAmTranslator, useMobileLayout, project } = rt

    return (
        <>
            {editing && <PortionEditor rt={rt} portion={portion} setEditing={setEditing} />}
            {copyModalOpen && (
                <CopyProjectDataModal
                    setOpenModal={setCopyModalOpen}
                    source={project}
                    copyDataType="portion"
                    copyData={portion}
                />
            )}
            <PortionName portion={portion} setEditing={setEditing} />
            <div className="portion-references">{rt.displayableReferences(portion.references)}</div>
            <div className="portion-notifications">
                <PortionNotificationList {...{ rt, portion }} />
            </div>
            {iAmTranslator && !useMobileLayout && (
                <PortionMenu
                    {...{
                        rt,
                        portion,
                        onDelete,
                        setEditing,
                        setCopyModalOpen
                    }}
                />
            )}
        </>
    )
})
interface ISortableElement {
    rt: Root
    portion: Portion
}

const PortionListItem = SortableElement(function (props: ISortableElement) {
    const { rt, portion } = props
    const { iAmInterpreter, useMobileLayout, username } = rt
    const { t } = useTranslation()

    function confirmDeletion(doDeletion: () => void) {
        // No need to confirm, if no video has been recorded yet
        if (!portion.videod()) {
            doDeletion()
            return
        }

        confirmAlert({
            title: t('recordingDeleteQuestion'),
            message: t('recordingExistsForPortion'),
            confirmLabel: t('recordingDelete'),
            cancelLabel: t('recordingKeep'),
            onConfirm: doDeletion
        })
    }

    function onDelete() {
        log('onDelete')
        const { project } = rt

        confirmDeletion(() => {
            project.removePortion(portion._id).catch(displayError)
        })
    }

    const assigned = portion.passages.some((passage) => passage.assignee === username && passage.task !== 'Finished')
    return (
        <div className={`portion-portion ${assigned ? 'passage-assigned' : ''}`}>
            {iAmInterpreter && !useMobileLayout && <DragHandle />}
            <PortionView portion={props.portion} rt={props.rt} onDelete={onDelete} />
        </div>
    )
})

export default PortionListItem
